import { JobPriorityType, JobStatusType, JobType } from '@/enum'
import type {
  IAssignedEmployee,
  IJob,
  IJobAssociatedAsset,
  IJobAssociatedAssetInfo,
  IJobAssociatedMaintenanceIssue,
  IJobAssociatedMaintenancePart,
  IJobAssociatedMaintenanceType,
  IJobAssociatedTaskSheet,
  IJobSchedule,
  IVendor
} from '@/interfaces'
import type { IDocument } from '@/interfaces/IDocument'
import Base from '../Base'
import DocumentModel from '../DocumentModel'
import JobAssociatedAsset from './JobAssociatedAsset'
import JobAssociatedAssetInfo from './JobAssociatedAssetInfo'
import JobAssociatedTaskSheet from './JobAssociatedTaskSheet'
import JobSchedule from './JobSchedule'
import type { IAppUser } from '@/interfaces/app-user/IAppUser'
import AppUser from '../AppUser'
import { Vendor } from '../vendors'
import type { IValueBasedTrigger } from '@/interfaces/jobs/IValueBasedTrigger'
import AssignedEmployee from '../employees/AssignedEmployee'

class Job extends Base implements IJob {
  title: string
  number: string
  description?: string
  type?: JobType
  asset?: IJobAssociatedAsset
  assetInfo?: IJobAssociatedAssetInfo
  status: JobStatusType
  priority?: JobPriorityType
  odometerTrigger?: IValueBasedTrigger
  machineHoursTrigger?: IValueBasedTrigger
  scheduledAt?: string
  dueAt?: string
  startedAt?: string
  completedAt?: string
  issueReporterId?: string
  issueReporter?: IAppUser
  assignedEmployeeIds?: string[]
  assignedEmployees?: IAssignedEmployee[]
  workOrderVendorId?: string
  workOrderVendor?: IVendor
  workOrderUrl?: string
  maintenanceTypes?: IJobAssociatedMaintenanceType[]
  maintenanceParts?: IJobAssociatedMaintenancePart[]
  taskSheets?: IJobAssociatedTaskSheet[]
  maintenanceIssues?: IJobAssociatedMaintenanceIssue[]
  documents?: IDocument[]
  notes?: string
  isEmployeeSafetyChecksCompleted?: boolean
  scheduleId?: string
  schedule: IJobSchedule

  constructor(job?: any) {
    super(job)
    this.title = job?.title ?? null
    this.number = job?.number
    this.description = job?.description ?? null
    this.type = job?.type
    if (job?.asset) {
      this.asset = new JobAssociatedAsset(job?.asset)
    }
    if (job?.assetInfo) {
      this.assetInfo = new JobAssociatedAssetInfo(job?.assetInfo)
    }
    this.status = job?.status ?? null
    this.priority = job?.priority ?? null
    this.odometerTrigger = job?.odometerTrigger
    this.machineHoursTrigger = job?.machineHoursTrigger
    this.scheduledAt = job?.scheduledAt
    this.dueAt = job?.dueAt
    this.startedAt = job?.startedAt
    this.completedAt = job?.completedAt
    this.issueReporterId = job?.issueReporterId
    this.issueReporter = new AppUser(job?.issueReporter)
    this.assignedEmployeeIds = job?.assignedEmployeeIds
    this.assignedEmployees = job?.assignedEmployees.map(
      (employee: any) => new AssignedEmployee(employee)
    )
    this.workOrderVendorId = job?.workOrderVendorId
    this.workOrderVendor = new Vendor(job?.workOrderVendor)
    this.workOrderUrl = job?.workOrderUrl
    this.maintenanceTypes = job?.maintenanceTypes
    this.maintenanceParts = job?.maintenanceParts
    this.maintenanceIssues = job?.maintenanceIssues
    this.taskSheets = job?.taskSheets?.map(
      (taskSheet: any) => new JobAssociatedTaskSheet(taskSheet)
    )
    this.documents = job?.documents?.map((document: any) => new DocumentModel(document))
    this.notes = job?.notes
    this.isEmployeeSafetyChecksCompleted = job?.isEmployeeSafetyChecksCompleted
    this.scheduleId = job?.scheduleId
    this.schedule = new JobSchedule(job?.schedule)
  }
}

export default Job
